import axios from "axios";
import { saveAs } from "file-saver";
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";

const pickupStatusColors = {
   pending: "#facc15",
   assigned: "#3b82f6",
   completed: "#22c55e",
   cancelled: "#ef4444",
   unassigned: "#9ca3af",
};

const bagStatusColors = {
   pending: "#facc15",
   posted: "#3b82f6",
   completed: "#22c55e",
};

export default function RedemSchedule() {
   const [activeTab, setActiveTab] = useState("pickup");
   const [rows, setRows] = useState([]);
   const [loading, setLoading] = useState(false);

   const rc_id = sessionStorage.getItem("rc_id"); // Redemption center ID

   const [fromDate, setFromDate] = useState("");
   const [toDate, setToDate] = useState("");
   const [search, setSearch] = useState("");

   const today = new Date();
   const [currentMonth, setCurrentMonth] = useState(today.getMonth());
   const [currentYear, setCurrentYear] = useState(today.getFullYear());
   const [selectedDate, setSelectedDate] = useState(null);

   const [expandedRow, setExpandedRow] = useState(null);

   const pillColors = ["#f87171", "#60a5fa", "#34d399", "#fbbf24", "#fb923c", "#22c55e", "#a78bfa"];

   const formatDate = (d) => {
      if (!d) return "";
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return d;
      return dt.toLocaleDateString("en-CA");
   };

   // Build calendar
   const monthDays = useMemo(() => {
      const firstDay = new Date(currentYear, currentMonth, 1);
      const lastDay = new Date(currentYear, currentMonth + 1, 0);
      const startDay = firstDay.getDay();
      const totalDays = lastDay.getDate();
      const daysArray = [];
      for (let i = 0; i < startDay; i++) daysArray.push(null);
      for (let d = 1; d <= totalDays; d++) {
         daysArray.push(new Date(currentYear, currentMonth, d));
      }
      return daysArray;
   }, [currentMonth, currentYear]);

   // Fetch data based on active tab & filters
   useEffect(() => {
      const fetchData = async () => {
         if (!rc_id) return;

         try {
            setLoading(true);
            const endpoint =
               activeTab === "pickup"
                  ? `/auth/redem/pickup-schedule/${rc_id}`
                  : `/auth/redem/bag-schedule/${rc_id}`;

            const params = {};
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;
            if (search) params.search = search;

            const res = await axios.get(endpoint, { params });
            setRows(res.data || []);
         } catch (err) {
            console.error("Failed to fetch schedule:", err);
            setRows([]);
         } finally {
            setLoading(false);
         }
      };

      fetchData();
   }, [activeTab, fromDate, toDate, search, rc_id]);

   const getStatusColor = (status) => {
      if (!status) return "#9ca3af";
      const key = String(status).toLowerCase();
      return activeTab === "pickup"
         ? pickupStatusColors[key] || "#3b82f6"
         : bagStatusColors[key] || "#3b82f6";
   };

   const filteredRows = rows.filter((r) => {
      const dateKey = activeTab === "pickup" ? "pickup_date" : "preferred_date";
      const rowDate = new Date(r[dateKey]);

      if (!selectedDate) {
         return (
            rowDate.getMonth() === currentMonth &&
            rowDate.getFullYear() === currentYear
         );
      }
      return formatDate(rowDate) === formatDate(selectedDate);
   });

   const exportToExcel = () => {
      if (!rows.length) return;
      const exportRows = rows.map((r) => {
         const base = {};
         columns.forEach((col) => {
            if (col.join) base[col.label] = col.join(r);
            else base[col.label] = r[col.key] ?? "";
         });
         return base;
      });

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], { type: "application/octet-stream" });
      saveAs(blob, `${activeTab}-schedule.xlsx`);
   };

   const resetFilters = () => {
      setFromDate("");
      setToDate("");
      setSearch("");
      setSelectedDate(null);
      setExpandedRow(null);
   };

   const columns =
      activeTab === "pickup"
         ? [
            { key: "cust_id", label: "Customer ID" },
            {
               key: "first_name",
               label: "Customer Name",
               join: (r) => `${r.first_name} ${r.last_name || ""}`,
            },
            { key: "pickup_address", label: "Pickup Address" },
            { key: "driver_assigned", label: "Driver ID" },
            { key: "driver_name", label: "Driver Name" },
            { key: "estimated_bags", label: "# Bag" },
            { key: "barcode", label: "Barcode #" },
            { key: "pickup_status", label: "Status" },
            { key: "pickup_date", label: "Date" },
            { key: "pickup_time", label: "Time" },
         ]
         : [
            { key: "cust_id", label: "Customer ID" },
            { key: "customer_name", label: "Customer Name" },
            { key: "pickup_address", label: "Pickup Address" },
            { key: "bags_count", label: "# Bag" },
            { key: "bag_code", label: "Bag Code" },
            { key: "send_via", label: "Send via" },
            { key: "send_status", label: "Status" },
            { key: "preferred_date", label: "Date" },
         ];

   const moveMonth = (delta) => {
      let newMonth = currentMonth + delta;
      let newYear = currentYear;
      if (newMonth < 0) {
         newMonth = 11;
         newYear--;
      } else if (newMonth > 11) {
         newMonth = 0;
         newYear++;
      }
      setCurrentMonth(newMonth);
      setCurrentYear(newYear);
      setSelectedDate(null);
   };

   const monthName = new Date(currentYear, currentMonth).toLocaleString(
      "default",
      { month: "long" }
   );

   return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
         <div className="max-w-full mx-auto">
            <div className="bg-white border border-gray-100 rounded-2xl shadow p-4 sm:p-6">
               {/* Header */}
               <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                  My Schedule Overview
               </h2>

               {/* Filters */}
               <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap mb-4">
                  <div className="flex gap-2 flex-wrap">
                     <div className="flex items-center gap-2 w-full sm:w-auto">
                        <label className="text-xs text-gray-600">From</label>
                        <input
                           type="date"
                           value={fromDate}
                           onChange={(e) => setFromDate(e.target.value)}
                           className="border rounded px-2 py-1 text-sm"
                        />
                     </div>
                     <div className="flex items-center gap-2 w-full sm:w-auto">
                        <label className="text-xs text-gray-600">To</label>
                        <input
                           type="date"
                           value={toDate}
                           onChange={(e) => setToDate(e.target.value)}
                           className="border rounded px-2 py-1 text-sm"
                        />
                     </div>
                  </div>
                  <input
                     type="text"
                     placeholder="Search by ID or Name..."
                     value={search}
                     onChange={(e) => setSearch(e.target.value)}
                     className="border rounded px-3 py-1 text-sm w-full sm:w-60"
                  />
                  <div className="flex gap-2 flex-wrap">
                     <button className="bg-black text-white px-3 py-1 rounded text-sm">
                        Search
                     </button>
                     <button
                        onClick={resetFilters}
                        className="bg-gray-200 px-3 py-1 rounded text-sm"
                     >
                        Reset
                     </button>
                     <button
                        onClick={exportToExcel}
                        className="bg-green-700 text-white px-3 py-1 rounded text-sm"
                     >
                        Export Excel
                     </button>
                  </div>
               </div>

               {/* Calendar */}
               <div className="max-w-xl mx-auto">
                  <div className="flex items-center justify-between mb-2">
                     <button
                        onClick={() => moveMonth(-1)}
                        className="px-2 py-1 bg-gray-800 text-white rounded"
                     >
                        &lt;
                     </button>
                     <h3 className="text-lg font-semibold text-gray-700">
                        {monthName} {currentYear}
                     </h3>
                     <button
                        onClick={() => moveMonth(1)}
                        className="px-2 py-1 bg-gray-800 text-white rounded"
                     >
                        &gt;
                     </button>
                  </div>

                  {/* Weekday headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                     {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map(
                        (day, idx) => (
                           <div
                              key={day}
                              style={{ backgroundColor: pillColors[idx] }}
                              className="text-white font-semibold py-2 rounded-lg text-center text-sm shadow-sm"
                           >
                              {day}
                           </div>
                        )
                     )}
                  </div>

                  {/* Days */}
                  <div className="grid grid-cols-7 gap-1 mb-4">
                     {monthDays.map((date, idx) => {
                        if (!date)
                           return (
                              <div
                                 key={idx}
                                 className="h-10 sm:h-14 bg-transparent rounded-lg"
                              />
                           );
                        const isToday =
                           formatDate(date) === formatDate(new Date());
                        const isSelected =
                           selectedDate &&
                           formatDate(selectedDate) === formatDate(date);
                        return (
                           <div
                              key={idx}
                              onClick={() =>
                                 setSelectedDate(isSelected ? null : date)
                              }
                              className={`h-10 sm:h-14 flex items-center justify-center text-sm font-semibold rounded-lg cursor-pointer transition-all duration-300 ${isSelected
                                 ? "ring-2 ring-lime-500 scale-105 shadow-lg"
                                 : isToday
                                    ? "border border-lime-400 bg-[#fffadf]"
                                    : "hover:bg-gray-100"
                                 }`}
                           >
                              {date.getDate()}
                           </div>
                        );
                     })}
                  </div>
               </div>

               {/* Tabs */}
               <div className="mt-2 border-b border-gray-200">
                  <nav className="-mb-px flex gap-4">
                     <button
                        onClick={() => setActiveTab("pickup")}
                        className={`py-2 px-3 text-sm font-medium ${activeTab === "pickup"
                           ? "border-b-4 border-lime-400 text-lime-600"
                           : "text-gray-500"
                           }`}
                     >
                        Pickup Requests
                     </button>
                     <button
                        onClick={() => setActiveTab("bag")}
                        className={`py-2 px-3 text-sm font-medium ${activeTab === "bag"
                           ? "border-b-4 border-lime-400 text-lime-600"
                           : "text-gray-500"
                           }`}
                     >
                        Bag Requests
                     </button>
                  </nav>
               </div>

               {/* Title */}
               <div className="mt-4 text-center">
                  <h3 className="text-lg sm:text-2xl font-bold text-lime-500">
                     {activeTab === "pickup"
                        ? "Pickup Requests Schedule"
                        : "Bag Requests Schedule"}
                  </h3>
               </div>

               {/* Legend */}
               <div className="mt-4 flex flex-wrap items-center gap-4 justify-center sm:justify-start">
                  {Object.entries(
                     activeTab === "pickup"
                        ? pickupStatusColors
                        : bagStatusColors
                  ).map(([status, color]) => (
                     <div key={status} className="flex items-center gap-2 text-xs">
                        <span
                           style={{ backgroundColor: color }}
                           className="w-3 h-3 rounded-full inline-block"
                        />
                        <span className="capitalize">{status}</span>
                     </div>
                  ))}
               </div>

               {/* Table */}
               <div className="mt-4 overflow-x-auto">
                  <table className="w-full table-auto border-collapse text-sm">
                     <thead>
                        <tr className="bg-gray-50">
                           {columns.map((col) => (
                              <th
                                 key={col.label}
                                 className="px-3 py-2 text-left border-b border-gray-200"
                              >
                                 {col.label}
                              </th>
                           ))}
                        </tr>
                     </thead>
                     <tbody>
                        {loading ? (
                           <tr>
                              <td
                                 colSpan={columns.length}
                                 className="p-6 text-center text-gray-500"
                              >
                                 Loading...
                              </td>
                           </tr>
                        ) : filteredRows.length === 0 ? (
                           <tr>
                              <td
                                 colSpan={columns.length}
                                 className="p-6 text-center text-gray-500"
                              >
                                 No records found
                              </td>
                           </tr>
                        ) : (
                           filteredRows.map((r, rowIndex) => (
                              <React.Fragment key={r.id || rowIndex}>
                                 <tr className="hover:bg-gray-50 transition">
                                    {columns.map((col) => {
                                       let val = col.join ? col.join(r) : r[col.key];
                                       if (
                                          col.key === "pickup_date" ||
                                          col.key === "preferred_date"
                                       )
                                          val = formatDate(val);

                                       // Barcode dropdown logic
                                       if (activeTab === "pickup" && col.key === "barcode") {
                                          const barcodes =
                                             typeof val === "string"
                                                ? val.split(",").map((b) => b.trim())
                                                : [];
                                          const hasMultiple = barcodes.length > 1;
                                          return (
                                             <td
                                                key={col.label}
                                                className="px-3 py-2 border-b border-gray-100 align-top"
                                             >
                                                {hasMultiple ? (
                                                   <div>
                                                      <button
                                                         onClick={() =>
                                                            setExpandedRow(
                                                               expandedRow === rowIndex
                                                                  ? null
                                                                  : rowIndex
                                                            )
                                                         }
                                                         className="text-blue-600 underline text-xs"
                                                      >
                                                         {expandedRow === rowIndex
                                                            ? "Hide Barcodes ▲"
                                                            : "View Barcodes ▼"}
                                                      </button>
                                                      {expandedRow === rowIndex && (
                                                         <ul className="mt-1 bg-gray-50 rounded-lg p-2 border border-gray-200 text-xs space-y-1 max-w-[150px]">
                                                            {barcodes.map((b, i) => (
                                                               <li
                                                                  key={i}
                                                                  className="px-2 py-1 bg-white rounded shadow-sm border text-center"
                                                               >
                                                                  {b}
                                                               </li>
                                                            ))}
                                                         </ul>
                                                      )}
                                                   </div>
                                                ) : (
                                                   <span>{val}</span>
                                                )}
                                             </td>
                                          );
                                       }

                                       // Status color
                                       if (
                                          col.key === "pickup_status" ||
                                          col.key === "send_status"
                                       ) {
                                          const color = getStatusColor(val);
                                          return (
                                             <td
                                                key={col.label}
                                                className="px-3 py-2 border-b border-gray-100"
                                             >
                                                <span className="inline-flex items-center gap-2">
                                                   <span
                                                      className="w-3 h-3 rounded-full inline-block"
                                                      style={{ backgroundColor: color }}
                                                   />
                                                   <span className="capitalize">{val}</span>
                                                </span>
                                             </td>
                                          );
                                       }

                                       return (
                                          <td
                                             key={col.label}
                                             className="px-3 py-2 border-b border-gray-100"
                                          >
                                             {val}
                                          </td>
                                       );
                                    })}
                                 </tr>
                              </React.Fragment>
                           ))
                        )}
                     </tbody>
                  </table>
               </div>

               <div className="mt-3 text-xs text-gray-500">
                  Showing {filteredRows.length} record
                  {filteredRows.length !== 1 ? "s" : ""} for{" "}
                  {selectedDate ? formatDate(selectedDate) : "All Dates"}.
               </div>
            </div>
         </div>
      </div>
   );
}
